export interface SwUpdateState {
  waiting: boolean;
  routeChangedSinceWaiting: boolean;
}

export type UpdateAction = "idle" | "wait" | "apply";

// Pure decision: apply a waiting update only after the user changes route
// (they have left whatever screen they were on — editor state is not lost).
export function nextUpdateAction(state: SwUpdateState): UpdateAction {
  if (!state.waiting) return "idle";
  return state.routeChangedSinceWaiting ? "apply" : "wait";
}

// Registers /sw.js and reports a worker that has reached the "waiting" state
// (installed, but not yet controlling the page).
export function registerServiceWorker(
  onWaiting: (worker: ServiceWorker) => void
): void {
  if (!("serviceWorker" in navigator)) return;

  const register = () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        if (reg.waiting) onWaiting(reg.waiting);
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              onWaiting(installing);
            }
          });
        });
      })
      .catch((err) => {
        // Graceful degrade: app runs without SW; installability/update unavailable.
        console.error("[pwa] service worker registration failed", err);
      });
  };

  // This runs from a post-paint useEffect. On warm-cache return visits the
  // window `load` event has usually already fired, so a `load` listener would
  // never run — register immediately when the document is already complete.
  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register, { once: true });
  }
}
